<script setup lang="ts">
/**
 * Finances tab — accountant view combining two data sources: clients with a
 * negative balance (`fetchClientsWithNegativeBalance`) and per-client visit
 * counts for a date range (`fetchClientVisitCountsByPeriod`), joined into
 * one alphabetical table with a column per (business_service_type_id, price)
 * variant plus revenue. Rows are flagged (and tinted) when a client has a
 * negative balance *and* no visits in the period — a negative balance alone
 * isn't worth calling out here.
 *
 * Mobile collapses the desktop's wide grid into two columns per
 * `mobileColumns` (name+flag / stacked balance-revenue-lessons), mirroring
 * `ClientsTab`'s `mobileColumns` pattern — same `DynamicTable`, same data,
 * just a narrower column set. The totals row (`FinanceTotalsRowCell`) is
 * desktop-only: the mobile sketch has no footer, so `tableRows` (which
 * appends it) is only used for the desktop columns.
 *
 * Loading is entirely manual: nothing refetches on date-input change, only
 * the "Reload" button actually calls the APIs, so an accountant can adjust
 * both dates before spending a round-trip. `Download CSV` exports exactly
 * the last-loaded range as `statement_<start>_<end>.csv`.
 */
import { computed, markRaw, onMounted, ref, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { useIsMobile } from '@/composables/useIsMobile'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'
import { fetchClientsWithNegativeBalance, type Client } from '@/api/clients'
import { fetchClientVisitCountsByPeriod, type ClientVisitCounts } from '@/api/businessServices'
import { fetchBusinessServiceTypes } from '@/api/businessServiceTypes'
import { majorToMinor } from '@/utils/majorToMinor'
import { formatMoney, moneyColumnLabel } from '@/utils/formatMoney'
import DynamicTable from '@/components/table/DynamicTable.vue'
import type { Column } from '@/components/table/DynamicTable.vue'
import RowIndexCell from '@/components/table/cell/RowIndexCell.vue'
import MoneyCell from '@/components/table/cell/MoneyCell.vue'
import PositiveIntegerCell from '@/components/table/cell/PositiveIntegerCell.vue'
import FinanceClientNameCell from '@/components/table/cell/FinanceClientNameCell.vue'
import FinanceTotalsRowCell from '@/components/table/cell/FinanceTotalsRowCell.vue'
import FinanceMobileLeftCell from '@/components/table/cell/FinanceMobileLeftCell.vue'
import FinanceMobileRightCell from '@/components/table/cell/FinanceMobileRightCell.vue'

interface PageLike<T> {
  // Minimal shape shared by every cursor-paginated response this tab drains.
  items: T[]
  has_next_page: boolean
  next_cursor: number | null
}

/** Repeatedly calls `fetchPage` until `has_next_page` is false, returning every item across all pages. */
async function drainAllPages<T>(
  fetchPage: (cursor?: number) => Promise<PageLike<T>>,
): Promise<T[]> {
  const all: T[] = []
  let cursor: number | undefined
  let hasNextPage = true
  while (hasNextPage) {
    const page = await fetchPage(cursor)
    all.push(...page.items)
    hasNextPage = page.has_next_page
    cursor = page.next_cursor ?? undefined
  }
  return all
}

interface ServiceVariant {
  // One (business_service_type_id, price) column in the table.

  key: string
  // Stable identity for this variant: `${business_service_type_id}:${price}`.

  businessServiceTypeId: string
  price: number | null
  name: string
  // Display label resolved from the service type's `description`, matching ServiceTypesPane's convention.
}

interface FinanceRow {
  // One rendered table row: a client with their balance and per-variant visit counts.

  kind: 'client'
  // Discriminant against `FinanceTotalsRow` in `tableRows`'s union, so
  // `pickRowOverride`/`rowClass` can narrow.

  clientId: string
  name: string
  rowNumber: number
  balance: number | null
  // Major-unit balance amount; null when the client has no negative-balance record (i.e. only appeared via visit counts).
  visitsByVariantKey: Map<string, number>
  totalVisits: number
  revenue: number
}

interface FinanceTotalsRow {
  // Synthetic row appended after the client rows, rendered by
  // `FinanceTotalsRowCell` via `pickRowOverride`. Never used for CSV export
  // (`downloadCsv` reads `rows`/`columnTotals` directly).

  kind: 'totals'
  clientCountLabel: string
  columnTotals: number[]
  grandTotalRevenueFormatted: string
}

type FinanceTableRow = FinanceRow | FinanceTotalsRow

const { isMobile } = useIsMobile()
const { t } = useI18n()
const { hasRole } = usePermissions()

/** `Date` → `YYYY-MM-DD` using local calendar fields (not `toISOString`, which would shift the date across a UTC day boundary for any non-UTC timezone). */
function isoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
function firstOfPreviousMonthIso(): string {
  const now = new Date()
  return isoDate(new Date(now.getFullYear(), now.getMonth() - 1, 1))
}
function firstOfCurrentMonthIso(): string {
  const now = new Date()
  return isoDate(new Date(now.getFullYear(), now.getMonth(), 1))
}

// Defaults to the full previous calendar month: `end` is the exclusive
// upper bound the backend expects (see `business_service_endpoints.py`'s
// `get_client_visit_counts_by_period`), so "first of this month" correctly
// includes every day of last month without including today's.
const startDate = ref(firstOfPreviousMonthIso())
const endDate = ref(firstOfCurrentMonthIso())

const loading = ref(false)
const error = ref<string | undefined>()
const variants = ref<ServiceVariant[]>([])
const rows = ref<FinanceRow[]>([])
/** The date range actually behind the currently displayed `rows` — distinct from the (possibly since-edited) date inputs, so the CSV filename always matches what's on screen. */
const loadedRange = ref<{ start: string; end: string } | null>(null)

const columnTotals = computed(() =>
  variants.value.map((variant) =>
    rows.value.reduce((sum, row) => sum + (row.visitsByVariantKey.get(variant.key) ?? 0), 0),
  ),
)
const grandTotalRevenue = computed(() => rows.value.reduce((sum, row) => sum + row.revenue, 0))

/** Flags clients who owe money *and* haven't shown up this period — a plain negative balance with visits isn't worth calling out. */
function isFlagged(row: FinanceRow): boolean {
  return row.balance !== null && row.balance < 0 && row.totalVisits === 0
}

function formatRevenue(revenue: number): string {
  const minorUnits = majorToMinor(revenue) ?? 0
  return formatMoney({ amount: minorUnits, includeCurrency: true }).formatted
}

/** Variant column label: `"<name> (<price>)"` when priced, else just `<name>`. Uses `formatMoney` (not a literal "$") so the currency/locale stay correct. */
function variantLabel(variant: ServiceVariant): string {
  if (variant.price === null) return variant.name
  const minorUnits = majorToMinor(variant.price) ?? 0
  const priceFormatted = formatMoney({ amount: minorUnits, includeCurrency: true }).formatted
  return `${variant.name} (${priceFormatted})`
}

async function reload() {
  loading.value = true
  error.value = undefined
  const requestedStart = startDate.value
  const requestedEnd = endDate.value
  try {
    const [negativeBalanceClients, visitCounts, serviceTypeGroups] = await Promise.all([
      // Negative balance requires additional permissions so check them and decide what to load.
      hasRole(ResourceAccessRole.ClientsNegativeBalanceView)
        ? drainAllPages<Client>((cursor) => fetchClientsWithNegativeBalance(cursor))
        : Promise.resolve<Client[]>([]),
      drainAllPages<ClientVisitCounts>((cursor) =>
        fetchClientVisitCountsByPeriod(requestedStart, requestedEnd, cursor),
      ),
      hasRole(ResourceAccessRole.BusinessServiceTypesView)
        ? drainAllPages((cursor) => fetchBusinessServiceTypes(cursor))
        : Promise.resolve([]),
    ])

    const typeNameById = new Map<string, string>()
    for (const group of serviceTypeGroups) {
      for (const type of group.business_service_types) {
        const label = type.description.trim()
        typeNameById.set(type.id, label !== '' ? label : t('common.typeFallback', { id: type.id }))
      }
    }

    const variantByKey = new Map<string, ServiceVariant>()
    for (const client of visitCounts) {
      for (const service of client.visited_services) {
        const key = `${service.business_service_type_id}:${service.price ?? 'null'}`
        if (!variantByKey.has(key)) {
          variantByKey.set(key, {
            key,
            businessServiceTypeId: service.business_service_type_id,
            price: service.price,
            name:
              typeNameById.get(service.business_service_type_id) ??
              t('common.typeFallback', { id: service.business_service_type_id }),
          })
        }
      }
    }
    const sortedVariants = [...variantByKey.values()].sort(
      (a, b) => a.name.localeCompare(b.name, 'ru') || (a.price ?? 0) - (b.price ?? 0),
    )

    const rowByClientId = new Map<
      string,
      {
        clientId: string
        name: string
        balance: number | null
        visitsByVariantKey: Map<string, number>
      }
    >()

    for (const client of negativeBalanceClients) {
      rowByClientId.set(client.id, {
        clientId: client.id,
        name: client.name,
        balance: client.current_balance?.amount ?? null,
        visitsByVariantKey: new Map(),
      })
    }
    for (const client of visitCounts) {
      const existingRow = rowByClientId.get(client.client_id)
      const row = existingRow ?? {
        clientId: client.client_id,
        name: client.client_name,
        balance: null,
        visitsByVariantKey: new Map<string, number>(),
      }
      for (const service of client.visited_services) {
        const key = `${service.business_service_type_id}:${service.price ?? 'null'}`
        row.visitsByVariantKey.set(key, service.visits_count)
      }
      rowByClientId.set(client.client_id, row)
    }

    const builtRows = [...rowByClientId.values()].map((row) => {
      let totalVisits = 0
      let revenue = 0
      for (const variant of sortedVariants) {
        const visits = row.visitsByVariantKey.get(variant.key) ?? 0
        totalVisits += visits
        revenue += visits * (variant.price ?? 0)
      }
      return { ...row, totalVisits, revenue }
    })
    builtRows.sort((a, b) => a.name.localeCompare(b.name, 'ru'))
    const finalRows: FinanceRow[] = builtRows.map((row, index) => ({
      ...row,
      kind: 'client' as const,
      rowNumber: index + 1,
    }))

    variants.value = sortedVariants
    rows.value = finalRows
    loadedRange.value = { start: requestedStart, end: requestedEnd }
  } catch (e) {
    error.value = e instanceof Error ? e.message : t('tabs.finances.loadError')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void reload()
})

// Column widths are wider than the ~48rem convention shared by the other
// tabs' fixed column sets: the number of variant columns is data-driven, so
// `.finances-scroll-wrap` (below) provides horizontal scroll instead.
const columns = computed<Column<FinanceRow>[]>(() => [
  {
    key: 'rowNumber',
    label: '#',
    component: markRaw(RowIndexCell),
    props: (row) => ({ index: row.rowNumber }),
    width: '3rem',
    align: 'center',
  },
  {
    key: 'name',
    label: t('common.client'),
    component: markRaw(FinanceClientNameCell),
    props: (row) => ({ name: row.name, flagged: isFlagged(row) }),
    width: '14rem',
  },
  {
    key: 'balance',
    label: moneyColumnLabel(t('common.balance')),
    component: markRaw(MoneyCell),
    props: (row) => ({ amount: majorToMinor(row.balance), emptyPlaceholder: '–' }),
    width: '7rem',
    align: 'center',
  },
  ...variants.value.map(
    (variant): Column<FinanceRow> => ({
      key: variant.key,
      label: variantLabel(variant),
      component: markRaw(PositiveIntegerCell),
      props: (row) => ({ value: row.visitsByVariantKey.get(variant.key) ?? 0 }),
      width: '6rem',
      align: 'center',
    }),
  ),
  {
    key: 'revenue',
    label: moneyColumnLabel(t('common.revenue')),
    component: markRaw(MoneyCell),
    props: (row) => ({ amount: majorToMinor(row.revenue) }),
    width: '7rem',
    align: 'center',
  },
])

const mobileColumns = computed<Column<FinanceRow>[]>(() => [
  {
    key: 'name',
    label: t('common.client'),
    component: markRaw(FinanceMobileLeftCell),
    props: (row) => ({ name: row.name, flagged: isFlagged(row) }),
    width: '24%',
  },
  {
    key: 'summary',
    label: t('tabs.finances.mobileSummary'),
    component: markRaw(FinanceMobileRightCell),
    props: (row) => ({
      balance: majorToMinor(row.balance),
      revenue: majorToMinor(row.revenue),
      // No price suffix here (unlike the desktop column headers): the
      // mobile sketch just lists lesson type names against their counts.
      lessons: variants.value
        .map((variant) => ({
          name: variant.name,
          price: variant.price,
          count: row.visitsByVariantKey.get(variant.key) ?? 0,
        }))
        .filter((lesson) => lesson.count > 0),
    }),
    align: 'right',
  },
])

/** Synthetic last row rendered by `FinanceTotalsRowCell` via `pickRowOverride`; omitted when there are no client rows so the empty state renders instead. */
const tableRows = computed<FinanceTableRow[]>(() =>
  rows.value.length > 0
    ? [
        ...rows.value,
        {
          kind: 'totals',
          clientCountLabel: t('tabs.finances.totalClients', rows.value.length),
          columnTotals: columnTotals.value,
          grandTotalRevenueFormatted: formatRevenue(grandTotalRevenue.value),
        },
      ]
    : [],
)

/** Tints a client row when flagged (negative balance + no visits this period; see `.row-negative-balance` below and `isFlagged`); the totals row is never tinted. */
function rowClass(row: unknown): string | undefined {
  const financeRow = row as FinanceTableRow
  if (financeRow.kind !== 'client') return undefined
  return isFlagged(financeRow) ? 'row-negative-balance' : undefined
}

/** Selects `FinanceTotalsRowCell` for the synthetic totals row so DynamicTable renders it full-width instead of the normal per-column cells. */
function pickRowOverride(row: unknown): Component | null {
  return (row as FinanceTableRow).kind === 'totals' ? markRaw(FinanceTotalsRowCell) : null
}

function escapeCsvField(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

function downloadCsv() {
  if (loadedRange.value === null) return
  const header = [
    '#',
    t('common.client'),
    t('common.balance'),
    ...variants.value.map((v) => variantLabel(v)),
    t('common.revenue'),
  ]
  const csvRows: string[][] = [header]
  rows.value.forEach((row, index) => {
    csvRows.push([
      String(index + 1),
      row.name,
      row.balance !== null ? row.balance.toFixed(2) : '',
      ...variants.value.map((v) => String(row.visitsByVariantKey.get(v.key) ?? 0)),
      row.revenue.toFixed(2),
    ])
  })
  const csvContent = csvRows.map((row) => row.map(escapeCsvField).join(',')).join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `statement_${loadedRange.value.start}_${loadedRange.value.end}.csv`
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
</script>

<template>
  <article class="finances-tab" :class="{ 'finances-tab--mobile': isMobile }">
    <header v-if="!isMobile" class="finances-header">
      <div>
        <h1>{{ t('tabs.finances.title') }}</h1>
        <p v-if="loadedRange" class="finances-period">
          {{ t('tabs.finances.period', { start: loadedRange.start, end: loadedRange.end }) }}
        </p>
      </div>
      <div class="finances-actions">
        <div class="finances-date-range">
          <label for="finances-start">{{ t('common.from') }}</label>
          <input id="finances-start" v-model="startDate" type="date" />
          <label for="finances-end">{{ t('common.to') }}</label>
          <input id="finances-end" v-model="endDate" type="date" />
        </div>
        <button
          type="button"
          class="finances-btn finances-btn--reload"
          :disabled="loading"
          @click="reload"
        >
          {{ loading ? t('common.loadingShort') : t('common.reload') }}
        </button>
        <button
          type="button"
          class="finances-btn finances-btn--download"
          :disabled="loading || loadedRange === null"
          @click="downloadCsv"
        >
          {{ t('common.downloadCsv') }}
        </button>
      </div>
    </header>

    <template v-else>
      <header class="finances-mobile-header">
        <h1>{{ t('tabs.finances.title') }}</h1>
        <p v-if="loadedRange" class="finances-period">
          {{ t('tabs.finances.period', { start: loadedRange.start, end: loadedRange.end }) }}
        </p>
      </header>
      <div class="finances-date-range finances-date-range--mobile">
        <label for="finances-start-mobile">{{ t('common.from') }}</label>
        <input id="finances-start-mobile" v-model="startDate" type="date" />
        <span class="finances-mobile-arrow" aria-hidden="true">→</span>
        <input
          id="finances-end-mobile"
          v-model="endDate"
          type="date"
          :aria-label="t('common.to')"
        />
      </div>
      <div class="finances-actions finances-actions--mobile">
        <button
          type="button"
          class="finances-btn finances-btn--reload"
          :disabled="loading"
          @click="reload"
        >
          {{ loading ? t('common.loadingShort') : t('common.reload') }}
        </button>
        <button
          type="button"
          class="finances-btn finances-btn--download"
          :disabled="loading || loadedRange === null"
          @click="downloadCsv"
        >
          {{ t('common.downloadCsv') }}
        </button>
      </div>
    </template>

    <p v-if="error" class="text-red-600">{{ error }}</p>

    <div
      v-if="!error"
      class="finances-scroll-wrap"
      :class="{
        'finances-scroll-wrap--loading': loading && rows.length > 0,
        'finances-scroll-wrap--mobile': isMobile,
      }"
    >
      <DynamicTable
        v-if="rows.length > 0"
        :class="isMobile ? 'finances-table--mobile' : undefined"
        :columns="(isMobile ? mobileColumns : columns) as Column<unknown>[]"
        :data="isMobile ? rows : tableRows"
        :row-class="rowClass"
        :row-override="isMobile ? undefined : pickRowOverride"
        :pinnable-header="!isMobile"
        storage-key="finances"
      />
      <p v-else-if="loading" class="text-tufte-muted finances-empty">
        {{ t('tabs.finances.loadingFull') }}
      </p>
      <p v-else class="finances-empty">
        {{ t('tabs.finances.empty') }}
      </p>
    </div>
  </article>
</template>

<style scoped>
.finances-header {
  display: flex;
  align-items: flex-end;
  /*
   * `flex-start` (not `space-between`) keeps the date-range/Reload/Download
   * controls clustered right next to the title. With the right conversation
   * flank collapsed for this tab (see `DesktopMain.vue`), the middle column
   * is wide enough that `space-between` would push the actions block all
   * the way to the far edge, visually detaching it from "Finances".
   */
  justify-content: flex-start;
  gap: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--color-tufte-border);
  margin-bottom: 0.25rem;
  flex-wrap: wrap;
}

.finances-period {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
  margin-top: 0.2rem;
}

.finances-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.finances-date-range {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
}

.finances-date-range input[type='date'] {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-text);
  background: var(--color-tufte-bg);
  border: 1px solid var(--color-tufte-border);
  border-radius: 6px;
  padding: 0.4rem 0.5rem;
  outline: none;
}

.finances-date-range input[type='date']:focus {
  border-color: color-mix(in srgb, var(--color-tufte-text) 55%, transparent);
}

.finances-btn {
  appearance: none;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-sm);
  border-radius: 6px;
  padding: 0.5rem 0.9rem;
  cursor: pointer;
  border: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
  color: var(--color-tufte-text);
}

.finances-btn:hover:not(:disabled) {
  background: color-mix(in srgb, var(--color-tufte-border) 25%, var(--color-tufte-bg));
}

.finances-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.finances-btn--download {
  background: var(--color-tufte-text);
  color: var(--color-tufte-bg);
  border-color: var(--color-tufte-text);
}

.finances-btn--download:hover:not(:disabled) {
  background: color-mix(in srgb, var(--color-tufte-text) 85%, transparent);
}

.finances-scroll-wrap {
  overflow-x: auto;
  margin-top: 1rem;
}

/*
 * Negative-balance client rows: tinted via DynamicTable's `rowClass` prop
 * (see `rowClass()`). `:deep()` is required because the `<td>` elements
 * belong to the per-column cell components (MoneyCell, FinanceClientNameCell,
 * etc.), not this component's own template.
 */
.finances-scroll-wrap :deep(.row-negative-balance td) {
  background: var(--color-surface-danger);
}
.finances-scroll-wrap :deep(.row-negative-balance:hover td) {
  background: color-mix(in srgb, var(--color-money-negative) 12%, var(--color-surface-danger));
}

.finances-empty {
  padding: 1.5rem 0.75rem;
  text-align: center;
  color: var(--color-tufte-muted);
  font-family: var(--font-tufte-sans);
  border: 1px solid var(--color-tufte-border);
  margin-top: 1rem;
}

/*
 * Mobile layout (see `docs/sketches/mobile/finances-table.html`): title/date-range/actions
 * stack full-width above a two-column list instead of sharing one header
 * row with the wide desktop grid. `MobileMain.vue` zeroes out the routed
 * `<article>`'s padding for every tab, so each section here carries its own
 * horizontal padding — same 1.25rem convention as `ClientsTab`'s
 * `.mobile-search-bar`/`.tab-header--mobile`.
 */
.finances-mobile-header {
  padding: 0.75rem 1.25rem;
}

.finances-date-range--mobile {
  padding: 0 1.25rem 0.75rem;
  border-bottom: 1px solid var(--color-tufte-border);
  margin-bottom: 0.75rem;
}

.finances-date-range--mobile input[type='date'] {
  flex: 1;
  min-width: 0;
}

.finances-mobile-arrow {
  color: var(--color-tufte-muted);
  flex-shrink: 0;
}

/*
 * Sticks to the top of `.mobile-main`'s scroll container once the header/
 * date-range above it scrolls out of view, so Reload/Download stay
 * reachable while scrolling through a long client list — same
 * `position: sticky; top: 0` technique as `ClientsTab`'s `.tab-header--mobile`.
 */
.finances-actions--mobile {
  padding: 0.75rem 1.25rem;
  flex-wrap: nowrap;
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--color-tufte-bg);
  border-bottom: 1px solid var(--color-tufte-border);
}

.finances-actions--mobile .finances-btn {
  flex: 1;
}

.finances-scroll-wrap--mobile {
  overflow-x: hidden;
  margin-top: 0;
}

/* Mobile table: full-width, roomier cell padding than the desktop grid's tight 0.35rem/0.5rem. */
.finances-scroll-wrap--mobile :deep(.dynamic-table) {
  width: 100%;
}

/*
 * Unlike `ClientsTab`'s mobile table (which hides its thead — the two
 * mobile columns there have no useful header text), Finances keeps a
 * visible header bar labeling the combined right column, matching the
 * sketch's small-caps "CLIENT" / "BALANCE · REVENUE · LESSONS" row.
 */
.finances-table--mobile :deep(thead th) {
  background: color-mix(in srgb, var(--color-tufte-border) 15%, var(--color-tufte-bg));
  color: var(--color-tufte-muted);
  font-size: 0.68rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.5rem 1.25rem;
}

.finances-table--mobile :deep(td) {
  padding: 0.7rem 1.25rem;
}

/* Name column is narrow (24% width, see mobileColumns) to leave room for the summary column; tighter padding keeps names from feeling squeezed. */
.finances-table--mobile :deep(.finance-mobile-left) {
  padding-left: 0.75rem;
  padding-right: 0.4rem;
}

.finances-tab--mobile > .text-red-600,
.finances-tab--mobile .finances-empty {
  margin-left: 1.25rem;
  margin-right: 1.25rem;
}
</style>
